import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { MenuOptionValue, Paginated, PaginationQueryWithFields } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { UpsertRolesPermissions, UpsertUser, User } from "./types";

export const useUsersApi = () => {
  const api = useApi();

  const getAdminUser = useCallback(
    (userId: string, signal: AbortSignal) => {
      const url = `/admin/users/${userId}`;
      return useSWRMutation<User | null, CustomError, string>(url, (url) => api.get<User>({ url, signal }));
    },
    [api],
  );

  const getAdminUsers = useCallback(
    async <K extends keyof User>(query: PaginationQueryWithFields<Pick<User, K>>, signal: AbortSignal) => {
      const url = buildUrl<Pick<User, K>>("/admin/users", query);
      const x = await api.get<Paginated<Pick<User, K>>>({ url, signal });
      return x.items;
    },
    [api],
  );

  const useAdminUsers = useCallback(
    <K extends keyof User>(
      query: PaginationQueryWithFields<Pick<User, K>>,
      signal: AbortSignal,
      subsidiaryId?: string,
      roleId?: string,
      carId?: string,
    ) => {
      const key = buildUrl<Pick<User, K>>(
        subsidiaryId
          ? `/admin/subsidiaries/${subsidiaryId}/users`
          : roleId
            ? `/admin/roles/${roleId}/users`
            : carId
              ? `/admin/cars/${carId}/drivers/users`
              : "/admin/users",
        query,
      );
      const url = key + (key.includes("users?") ? "&" : "?") + "include_hidden=true";

      return useSWR<Paginated<Pick<User, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Pick<User, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminUserTeamMembers = useCallback(
    <K extends keyof User>(userId: string, query: PaginationQueryWithFields<Pick<User, K>>, signal: AbortSignal) => {
      const key = buildUrl<Pick<User, K>>(`/admin/users/${userId}/team_members`, query);
      const url = key + (key.includes("?") ? "&" : "?") + "include_hidden=true";

      return useSWR<Paginated<Pick<User, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Pick<User, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminUser = useCallback(
    async (body: UpsertUser): Promise<{ id: string }> => {
      const url = `/admin/users`;
      const id = await api.post<UpsertUser, { id: string }>(url, body);
      api.mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminUser = useCallback(
    async (id: string, body: UpsertUser) => {
      const url = `/admin/users/${id}`;
      await api.put<UpsertUser>(url, body);
      api.mutateCache([
        `/admin/users`,
        `/admin/drivers`,
        `/admin/due_dates/status`,
        `/admin/car_tasks/status`,
        `/admin/subsidiaries`,
      ]);
    },
    [api],
  );

  const updateAdminUserRolesPermissions = useCallback(
    async (id: string, body: UpsertRolesPermissions) => {
      const url = `/admin/users/${id}/roles`;
      await api.put<UpsertRolesPermissions>(url, body);
      api.mutateCache([
        `/admin/users`,
        `/admin/drivers`,
        `/me/settings`,
        `/admin/users/${id}`,
        `/admin/subsidiaries`,
        `/admin/users/${id}/roles`,
      ]);
    },
    [api],
  );

  const deleteAdminUser = useCallback(
    async (userId: string): Promise<void> => {
      const url = `/admin/users/${userId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/users`, `/admin/drivers`]);
      });
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/users/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminDepartments = useCallback(
    (signal: AbortSignal) => {
      const url = "/admin/users/departments";
      return useSWR<string[], CustomError, string>(url, (url) => api.get<string[]>({ url, signal }));
    },
    [api],
  );

  const deleteAdminUserSubsidiary = useCallback(
    async (subsidiaryId: string, userId: string): Promise<void> => {
      const url = `/admin/subsidiaries/${subsidiaryId}/users/${userId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/users`, `/admin/subsidiaries`, `/admin/subsidiaries/${subsidiaryId}/users`]);
      });
    },
    [api],
  );

  const addAdminUserSubsidiary = useCallback(
    async (subsidiaryId: string, userId: string): Promise<void> => {
      const url = `/admin/subsidiaries/${subsidiaryId}/users/${userId}`;
      await api.put(url, undefined).then(() => {
        api.mutateCache([`/admin/users`, `/admin/subsidiaries`, `/admin/subsidiaries/${subsidiaryId}/users`]);
      });
    },
    [api],
  );

  const deleteAdminUserRole = useCallback(
    async (roleId: string, userId: string): Promise<void> => {
      const url = `/admin/roles/${roleId}/users/${userId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/users`, `/admin/roles`, `/admin/roles/${roleId}/users`]);
      });
    },
    [api],
  );

  const addAdminUserRole = useCallback(
    async (roleId: string, userId: string): Promise<void> => {
      const url = `/admin/roles/${roleId}/users/${userId}`;
      await api.put(url, undefined).then(() => {
        api.mutateCache([`/admin/users`, `/admin/roles`, `/admin/roles/${roleId}/users`]);
      });
    },
    [api],
  );

  const getAdminCurrentDriverUser = useCallback(() => {
    const url = `/admin/cars/{0}/current_driver/user`;
    return useSWRMutation<User, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
      api.get<User>({ url: url.replace("{0}", arg) }),
    );
  }, [api]);

  return {
    updateAdminUser,
    insertAdminUser,
    getAdminUsers,
    useAdminUsers,
    useAdminUserTeamMembers,
    getAdminUser,
    deleteAdminUser,
    getFieldFilters,
    useAdminDepartments,
    updateAdminUserRolesPermissions,

    deleteAdminUserSubsidiary,
    addAdminUserSubsidiary,

    deleteAdminUserRole,
    addAdminUserRole,
    getAdminCurrentDriverUser,
  };
};
